import firebase from 'firebase/app';
import 'firebase/firestore';
import { useCallback, useState } from 'react';
import { useCollection } from 'react-firebase-hooks/firestore';

export const usePaginatedQuery = <T extends firebase.firestore.DocumentData>(
  baseQuery: firebase.firestore.Query<T>,
  limit: number
) => {
  const limitedQuery = baseQuery.limit(limit);

  const [query, setQuery] = useState(limitedQuery);

  const [previousPaginations, setPreviousPaginations] = useState<
    firebase.firestore.QueryDocumentSnapshot<T>[]
  >([]);

  const [result, loading, error] = useCollection<T>(query);

  const previous = useCallback(() => {
    const copy = [...previousPaginations];
    const previous = copy.pop();
    setQuery(limitedQuery.startAt(previous));
    setPreviousPaginations(copy);
  }, [limitedQuery, previousPaginations]);

  const next = useCallback(() => {
    if (!result) {
      return;
    }

    const last = result.docs[result.docs.length - 1];
    setQuery(limitedQuery.startAfter(last));
    setPreviousPaginations((p) => p.concat(result.docs[0]));
  }, [result, limitedQuery]);

  const hasNext = (result?.docs.length ?? 0) > 0;
  const hasPrevious = previousPaginations.length > 0;

  return { result, next, previous, hasNext, hasPrevious, loading, error };
};
